import { cn } from "@gymflow/helpers";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { useState } from "react";

import Label from "./base/Label";
import { Spinner } from "./Spinner";

export interface SwitchProps {
  value: boolean;
  onChange: (checked: boolean) => void | Promise<void>;
  disabled?: boolean;
  className?: string;
}

export type LabeledSwitchProps = {
  label?: string;
  labelStyle?: "horizontal" | "vertical";
  isRequired?: boolean;
  helpTooltip?: string;
  isOptional?: boolean;
} & SwitchProps;

export function LabeledSwitch({
  label,
  value,
  onChange,
  disabled,
  className,
  labelStyle = "horizontal",
  isRequired,
  isOptional,
  helpTooltip,
}: LabeledSwitchProps) {
  return (
    <div
      className={cn("flex flex-row items-center gap-2", {
        "items-start flex-col-reverse": labelStyle === "vertical",
      })}
    >
      <Switch
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={className}
      />
      {label && (
        <Label
          label={label}
          isRequired={isRequired}
          helpTooltip={helpTooltip}
          isOptional={isOptional}
        />
      )}
    </div>
  );
}
function Switch({
  className,
  thumbClassName,
  ...props
}: Omit<
  React.ComponentProps<typeof SwitchPrimitive.Root>,
  "value" | "onChange"
> & {
  value: boolean;
  onChange: (checked: boolean) => void | Promise<void>;
  thumbClassName?: string;
}) {
  const { value, onChange, ...rest } = props;
  const [isLoading, setIsLoading] = useState(false);
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      className={cn(
        "relative peer data-[state=checked]:bg-secondary-500 data-[state=unchecked]:bg-gray-200 inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-transparent shadow-xs transition-all disabled:cursor-not-allowed disabled:opacity-50 !outline-none",
        className,
      )}
      {...rest}
      checked={props.value}
      disabled={isLoading || props.disabled}
      onCheckedChange={async (checked) => {
        const result = props.onChange(checked);
        if (result instanceof Promise) {
          setIsLoading(true);
          try {
            await result;
          } catch {
            setIsLoading(false);
          }
          setIsLoading(false);
        }
      }}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          "bg-gray-0 pointer-events-none block size-5  rounded-full ring-0 transition-all data-[state=checked]:ml-[calc(100%-1.25rem-1px)] data-[state=unchecked]:ml-px",
          thumbClassName,
        )}
      />
      {isLoading && (
        <Spinner className="absolute inset-0 z-10 flex size-[calc(100%-4px)] items-center justify-center" />
      )}
    </SwitchPrimitive.Root>
  );
}
export { Switch };
